import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { UserInfo } from "../Models/UserInfo";
import * as authService from "../Services/Http/AuthService";
import React from "react";
import axios from "axios";
import { ApiError } from "../Models/ApiError";
import { RefreshTokenRequest } from "../Models/RefreshTokenRequest";
import { useNavigate } from "react-router";
import { v4 as uuidv4 } from "uuid";
import { dataContext } from "./contextProvider";
import { observer } from "mobx-react";
import tempStore from "../Store/tempStore";
import { LoginToServerResponse } from "../Models/LoginToServerResponse";
import { LoginToServerRequest } from "../Models/LoginToServerRequest";
import { LoginToDatabaseRequest } from "../Models/LoginToDatabaseRequest";
import { LogoutRequest } from "../Models/LogoutRequest";
import { DeviceActivationCheckResponse } from "../Models/DeviceActivationCheckResponse";
import { checkDeviceActivationAPI } from "../Services/Http/ActivationService";
import { CheckActivationResult } from "../Models/CheckActivationResult";

const ENV_PROVIDER = process.env.REACT_APP_PROVIDER;
const ENV_CALL_SOURCE = process.env.REACT_APP_CALL_SOURCE;
const ENV_CALL_VERSION = process.env.REACT_APP_VERSION;

type UserContextType = {
  getUser: () => UserInfo | null;
  init: () => void;
  refreshToken: () => Promise<UserInfo | null>;
  isRefreshing: () => boolean;
  getDeviceId: () => string | null;
  loginToServer: (model: LoginToServerRequest) => Promise<LoginToServerResponse | null>;
  loginToDatabase: (model: LoginToDatabaseRequest) => Promise<UserInfo | null>;
  logout: (model: LogoutRequest) => Promise<string | null>;
  isAuthenticated: () => boolean;
  getSetting: (serverUrl: string, dbKey: string, settingName: string) => string | undefined;
  getSettingNumber: (serverUrl: string, dbKey: string, settingName: string) => number;
  isAllowed: (serverUrl: string, dbKey: string, permissionName: string) => boolean;
  initActivation: () => Promise<CheckActivationResult>;
};

type Props = { children: React.ReactNode };

const UserContext = createContext<UserContextType>({} as UserContextType);

export const UserProvider = observer(({ children }: Props) => {
  const [isReady, setIsReady] = useState(false);
  const refreshTokenKey = `e_refresh_token_${ENV_PROVIDER}`;
  const platformIdKey = `e_platform_id_${ENV_PROVIDER}`;
  const isTokenRefreshing = useRef<boolean>(false);
  const navigate = useNavigate();
  const dataStore = useContext(dataContext);
  const isRefreshing = useCallback(() => {
    return isTokenRefreshing?.current;
  }, [isTokenRefreshing]);
  const isCheckingActivation = useRef<boolean>(false);

  const _filteredUrlPatterns: RegExp[] = [/auth\/loginserver-check|auth\/loginserver|activation\/check|activation\/activate/];
  const shouldBypass = (url: string): boolean => {
    return _filteredUrlPatterns.some((e) => {
      return e.test(url);
    });
  };

  const isExpired = (expiresAt?: number): boolean => {
    if (!expiresAt) return true;
    const now = Date.now();
    //return now > expiresAt - 60000; // add a gap of 60 seconds before expiration end
    return now > expiresAt - 2000; // add a gap of 2 seconds before expiration end
  };
  // Add a request interceptor and attach Authorization bearer token
  axios.interceptors.request.use(
    async (config) => {
      const deviceId = getDeviceId();

      // Add device-id header to every request
      config.headers["device-id"] = deviceId ? deviceId : "";
      // Warehouse location chosen at login - the API keeps the put away cache per location
      config.headers["location-no"] = dataStore.location ? String(dataStore.location) : "";
      let tokenToAttach = "";
      const _shouldBypass = config?.url ? shouldBypass(config.url) : true;
      if (!_shouldBypass) {
        try {
          const user: UserInfo | null = getUser();
          if (user) {
            if (user.token && !isExpired(user.expiresAt)) {
              tokenToAttach = user.token;
            } else if (user.refreshToken && !isTokenRefreshing.current) {
              //if not refreshing alaready. try to refresh token
              const refreshedUser = await refreshToken();
              if (refreshedUser && refreshedUser.token && refreshedUser.token !== user.token && !isExpired(refreshedUser.expiresAt)) {
                tokenToAttach = refreshedUser.token;
              } else {
                // token was not refreshed successfully, navigate to logout
                navigate("/logout");
              }
            } else if (isTokenRefreshing.current && user.token) {
              //still user the old token while refreshing because we still have 60 seconds before expiration end
              tokenToAttach = user.token;
            }
          }
          if (tokenToAttach) {
            config.headers.Authorization = `Bearer ${tokenToAttach}`;
          } else {
            config.headers.Authorization = "";
          }
          return config;
        } catch (error) {
          console.error("Error getting access token:", error);
          return Promise.reject(error);
        }
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Add a response interceptor to handle 403 errors globally
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const prevRequest = error?.config;
      if (error?.response?.status === 403 && !prevRequest?.sent) {
        prevRequest.sent = true;
        const newtoken: UserInfo | null = await refreshToken();
        if (newtoken && newtoken.token) {
          prevRequest.headers["Authorization"] = `Bearer ${newtoken.token}`;
          return axios(prevRequest);
        } else {
          prevRequest.headers["Authorization"] = "";
        }
      } else if (error?.response?.status === 401) {
        //unauthorized, token expired or revoked, navigate to logout
        navigate("/logout");
      }
      return Promise.reject(error);
    }
  );

  const setRefreshTokenStorage = useCallback(
    (refreshToken: string | null): string | null => {
      if (refreshToken) {
        localStorage.setItem(refreshTokenKey, refreshToken);
      } else {
        localStorage.removeItem(refreshTokenKey);
      }
      return refreshToken;
    },
    [refreshTokenKey]
  );

  const getRefreshTokenStorage = useCallback((): string | null => {
    const str = localStorage.getItem(refreshTokenKey);
    if (str) {
      return str;
    }
    return null;
  }, [refreshTokenKey]);

  const setPlatformIdStorage = useCallback(
    (platformId: string | null): string | null => {
      if (platformId) {
        localStorage.setItem(platformIdKey, platformId);
      } else {
        localStorage.removeItem(platformIdKey);
      }
      return platformId;
    },
    [platformIdKey]
  );

  const getPlatformIdStorage = useCallback((): string | null => {
    const str = localStorage.getItem(platformIdKey);
    if (str) {
      return str;
    }
    return null;
  }, [platformIdKey]);

  const getDeviceId = useCallback((): string | null => {
    return getPlatformIdStorage();
  }, [getPlatformIdStorage]);

  const setUser = useCallback(
    (user: UserInfo | null): void => {
      dataStore.userInfo = user;
    },
    [dataStore]
  );

  const getUser = useCallback((): UserInfo | null => {
    return dataStore.userInfo;
  }, [dataStore.userInfo]);

  const init = () => {
    const refreshTokenFromStorage = getRefreshTokenStorage();
    //if we have refresh token but not user, try to init again
    if (refreshTokenFromStorage && !getUser()) {
      return initLocal();
    }
  };

  const initActivation = useCallback(async (): Promise<CheckActivationResult> => {
    const ret: CheckActivationResult = {};
    if (dataStore.serverUrl && dataStore.serverUrl === dataStore.licenseValidatedServerUrl) {
      ret.alreadyChecked = true;
      return ret; //already checked activation
    }
    if (isCheckingActivation.current) {
      ret.busy = true;
      return ret;
    }
    try {
      isCheckingActivation.current = true;
      const deviceId = getDeviceId();
      if (!deviceId) {
        console.log("Device ID not found. Please restart the application.");
        ret.deviceIdNotFound = true;
        return ret;
      }
      const resp: DeviceActivationCheckResponse = await checkDeviceActivationAPI(deviceId!);
      dataStore.permittedServers = resp.permittedServerUrls ? [...resp.permittedServerUrls] : [];
      if (resp.active) {
        if (!resp.permittedServerUrls?.find((u) => u === dataStore.licenseValidatedServerUrl)) {
          ret.setShowDeviceRegistration = true;
        } else if (dataStore.serverUrl) {
          dataStore.licenseValidatedServerUrl = dataStore.serverUrl;
          ret.serverUrl = dataStore.serverUrl;
          ret.continueToCompleteRegistration = true;
          //completeRegistration(dataStore.serverUrl);
        } else {
          ret.serverUrl = "";
          ret.continueToCompleteRegistration = true;
          //completeRegistration("");
        }
      } else {
        ret.setShowDeviceRegistration = true;
        //setShowDeviceRegistration(true);
      }
    } catch (err) {
      dataStore.permittedServers = [];
      dataStore.licenseValidatedServerUrl = null;
      dataStore.serverUrl = null;
    } finally {
      isCheckingActivation.current = false;
    }
    return ret;
  }, [dataStore, getDeviceId]);

  // Each time dataStore.serverUrl is changed, check device activation, and also on init
  useEffect(() => {
    initActivation();
  }, [dataStore.serverUrl, dataStore.licenseValidatedServerUrl, getDeviceId, initActivation]);

  const callRefreshToken = useCallback(
    async (refreshToken: string): Promise<UserInfo | null> => {
      const model: RefreshTokenRequest = {
        refreshToken,
        deviceId: getDeviceId() || "",
      };
      try {
        const user: UserInfo | null = await authService.refreshTokenAPI(model);
        return user;
      } catch (err: any) {
        throw err;
      }
    },
    [getDeviceId]
  );

  const refreshToken = useCallback(
    async (existingRefreshToken?: string): Promise<UserInfo | null> => {
      // there is a refresh token saved, try to refresh
      isTokenRefreshing.current = true;
      setUser(null);
      try {
        const currentRefreshToken = existingRefreshToken || getRefreshTokenStorage();
        if (!currentRefreshToken) {
          return null;
        }
        const userInfo: UserInfo | null = await callRefreshToken(currentRefreshToken);
        isTokenRefreshing.current = false;
        if (userInfo) {
          console.log("Token refreshed successfully");
          setUser({ ...userInfo });
          setRefreshTokenStorage(userInfo?.refreshToken || null);
        } else {
          console.log("Token was not refreshed");
          setRefreshTokenStorage(null);
        }
        return userInfo;
      } catch (err) {
        const error = err as ApiError;
        isTokenRefreshing.current = false;
        if (error.status !== undefined && error.status === 401) {
          //token expired or revoked, then remove the token from local storage and redirect to login page
          console.log("Token expired or has been revoked");
          setRefreshTokenStorage(null);
          throw error;
        }
        return null;
      }
    },
    [setUser, getRefreshTokenStorage, callRefreshToken, setRefreshTokenStorage]
  );

  const initLocal = useCallback(async () => {
    axios.defaults.headers.common["Call-Source"] = ENV_CALL_SOURCE;
    axios.defaults.headers.common["Call-Version"] = ENV_CALL_VERSION;
    // if we want to send cookies along with the request we need to set withCredentials = true; and the backend needs to AllowCredentials = true and set hosts for CORS
    //axios.defaults.withCredentials = true;

    //if we don't have platformId, then create one
    if (!getPlatformIdStorage()) {
      setPlatformIdStorage(uuidv4());
    }

    // on first open try to refresh the token
    const currentRefreshToken = getRefreshTokenStorage();
    if (!currentRefreshToken) {
      // no refresh token, do auto register
      dataStore.clear();
      console.log("No refresh token found, data store cleared");
      setIsReady(true);
      navigate("/login");
    } else if (currentRefreshToken && !isTokenRefreshing.current) {
      // there is a refresh token saved, try to refresh
      try {
        const userInfo: UserInfo | null = await refreshToken(currentRefreshToken);
        if (userInfo) {
          setUser({ ...userInfo });
          // if no server or db selected, navigate to login
          const hasPermissions = userInfo.dbPermission && userInfo.dbPermission.serverUrl && userInfo.dbPermission.companyNo;
          if (!dataStore.serverUrl || !dataStore.dbKey || dataStore.serverUrl !== userInfo.dbPermission?.serverUrl || !hasPermissions) {
            navigate("/login");
          }
        } else {
          // if error, clear user permissions and navigate to login
          setUser(null);
          initLocal();
          navigate("/login");
        }
        setIsReady(true);
      } catch (err) {
        const error = err as ApiError;
        setIsReady(true);
        if (error.status !== undefined && error.status === 403) {
          //token expired or revoked, then remove the token from local storage, init again and redirect to login page
          setRefreshTokenStorage(null);
          initLocal();
          navigate("/login");
        }
      }
    }
  }, [getPlatformIdStorage, getRefreshTokenStorage, setPlatformIdStorage, navigate, refreshToken, setUser, dataStore, setRefreshTokenStorage]);

  useEffect(() => {
    initLocal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearDataStore = () => {
    dataStore.clear();
  };
  const clearTempStore = () => {
    tempStore.clear();
  };

  const isAuthenticated = (): boolean => {
    return !!dataStore.userInfo;
  };

  const loginToServer = async (model: LoginToServerRequest): Promise<LoginToServerResponse | null> => {
    try {
      const resp: LoginToServerResponse | null = await authService.loginToServerAPI(model);
      return resp;
    } catch (err) {
      const error = err as ApiError;
      console.error(error?.message || error);
    }
    return null;
  };

  const loginToDatabase = async (model: LoginToDatabaseRequest): Promise<UserInfo | null> => {
    try {
      const userInfo: UserInfo | null = await authService.loginToDatabaseAPI(model);
      if (userInfo) {
        setUser({ ...userInfo });
        setRefreshTokenStorage(userInfo?.refreshToken || null);
      } else {
        setRefreshTokenStorage(null);
      }
      return userInfo;
    } catch (err) {
      const error = err as ApiError;
      console.error(error?.message || error);
    }
    return null;
  };

  const getSetting = (serverUrl: string, dbKey: string, settingName: string): string | undefined => {
    if (dataStore.userInfo?.dbPermission?.serverUrl === serverUrl && dataStore.userInfo?.dbPermission?.dbKey === dbKey) {
      return dataStore.userInfo?.settings?.[settingName];
    }
    return undefined;
  };

  const getSettingNumber = (serverUrl: string, dbKey: string, settingName: string): number => {
    const strVal: string | undefined = getSetting(serverUrl, dbKey, settingName);
    if (!strVal || !strVal.trim() || isNaN(Number(strVal.trim()))) {
      return 0;
    }

    let numVal = 0;
    if (strVal) {
      numVal = parseInt(strVal.trim(), 10);
    }
    return numVal;
  };

  const isAllowed = (serverUrl: string, dbKey: string, permissionName: string): boolean => {
    if (dataStore.userInfo?.dbPermission?.serverUrl === serverUrl && dataStore.userInfo?.dbPermission?.dbKey === dbKey) {
      return dataStore.userInfo?.esuitePermissions?.[permissionName] === "true";
    }
    return false;
  };

  const logout = async (model: LogoutRequest): Promise<string | null> => {
    try {
      const result: string | null = await authService.logoutAPI(model);
      clearDataStore();
      clearTempStore();
      setUser(null);
      setRefreshTokenStorage(null);
      return result;
    } catch (err) {
      const error = err as ApiError;
      console.error(error?.message || error);
    }
    return null;
  };

  return (
    <UserContext.Provider
      value={{
        getUser,
        init,
        refreshToken,
        isRefreshing,
        getDeviceId,
        loginToServer,
        loginToDatabase,
        logout,
        isAuthenticated,
        getSetting,
        getSettingNumber,
        isAllowed,
        initActivation,
      }}
    >
      {isReady ? children : "Loading..."}
    </UserContext.Provider>
  );
});

export const useAuth = () => React.useContext(UserContext);
